import { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Plus, Trash2, Edit2, X, Users, Building, Globe, Key, ChevronDown, ChevronUp,
  Copy, Check, ToggleLeft, ToggleRight, Server, Cloud, Download, Upload,
  Search, Filter, Mail, Send
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LICENSE_TYPES = [
  { value: "standard", label: "Standard" },
  { value: "premium", label: "Premium" },
  { value: "enterprise", label: "Entreprise" },
];

const ALGO_LABELS = {
  "hmac-sha256": "ClaimFlow (HMAC-SHA256)",
  "simple-hash": "ProjetHub (Hash simple)",
  "generic-hmac": "Generique (HMAC personnalise)",
};

// ==================== CLIENT MANAGER ====================
export const ClientManager = ({ headers }) => {
  const [clients, setClients] = useState([]);
  const [generators, setGenerators] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedClient, setExpandedClient] = useState(null);
  const [showLicenseForm, setShowLicenseForm] = useState(null);
  const [copied, setCopied] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", address: "", siret: "", sites: [], notes: "", ca_tracking: { abonnements_mensuels: 0, abonnements_annuels: 0, services: 0, tma: 0 }, partner_id: "" });
  const [licForm, setLicForm] = useState({ client_id: "", generator_id: "", site_url: "", deployment_type: "onpremise", license_type: "", max_users: 1, duration_days: 0, expires_at: "", notes: "" });
  const [sendingEmail, setSendingEmail] = useState(null);
  const [partners, setPartners] = useState([]);

  const fetchAll = async () => {
    try {
      const [c, g, l] = await Promise.all([
        axios.get(`${API}/admin/clients`, { headers }),
        axios.get(`${API}/admin/generators`, { headers }),
        axios.get(`${API}/admin/licenses`, { headers }),
      ]);
      setClients(c.data);
      setGenerators(g.data);
      setLicenses(l.data);
      // Fetch partners
      try {
        const usersRes = await axios.get(`${API}/admin/users`, { headers });
        setPartners(usersRes.data.filter(u => u.role === "partenaire"));
      } catch (e) {}
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchAll(); }, []);

  const resetForm = () => { setForm({ name: "", email: "", phone: "", company: "", address: "", siret: "", sites: [], notes: "", ca_tracking: { abonnements_mensuels: 0, abonnements_annuels: 0, services: 0, tma: 0 }, partner_id: "" }); setEditingId(null); setShowForm(false); };

  const handleEdit = (client) => { setForm({ name: client.name, email: client.email || "", phone: client.phone || "", company: client.company || "", address: client.address || "", siret: client.siret || "", sites: client.sites || [], notes: client.notes || "", ca_tracking: client.ca_tracking || { abonnements_mensuels: 0, abonnements_annuels: 0, services: 0, tma: 0 }, partner_id: client.partner_id || "" }); setEditingId(client.id); setShowForm(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/admin/clients/${editingId}`, form, { headers });
      } else {
        await axios.post(`${API}/admin/clients`, form, { headers });
      }
      resetForm();
      fetchAll();
    } catch (err) { console.error(err); }
  };

  const deleteClient = async (id) => {
    if (!window.confirm("Supprimer ce client et toutes ses licences ?")) return;
    await axios.delete(`${API}/admin/clients/${id}`, { headers });
    fetchAll();
  };

  const addSite = () => { setForm({ ...form, sites: [...form.sites, { url: "", software_name: "", deployment_type: "onpremise", notes: "" }] }); };
  const updateSite = (i, field, val) => { const s = [...form.sites]; s[i][field] = val; setForm({ ...form, sites: s }); };
  const removeSite = (i) => { setForm({ ...form, sites: form.sites.filter((_, idx) => idx !== i) }); };

  const selectedGenerator = generators.find(g => g.id === licForm.generator_id);

  const handleLicenseSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/licenses`, licForm, { headers });
      setShowLicenseForm(null);
      fetchAll();
    } catch (err) { alert(err.response?.data?.detail || "Erreur"); }
  };

  const toggleLicense = async (id) => {
    await axios.put(`${API}/admin/licenses/${id}/toggle`, {}, { headers });
    fetchAll();
  };

  const deleteLicense = async (id) => {
    if (!window.confirm("Supprimer cette licence ?")) return;
    await axios.delete(`${API}/admin/licenses/${id}`, { headers });
    fetchAll();
  };

  const copyKey = (key) => {
    navigator.clipboard.writeText(key);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const sendLicenseEmail = async (licId) => {
    setSendingEmail(licId);
    try {
      await axios.post(`${API}/admin/licenses/${licId}/email`, {}, { headers });
      alert("Licence envoyée par email avec succès !");
    } catch (err) {
      alert(err.response?.data?.detail || "Erreur lors de l'envoi");
    }
    setSendingEmail(null);
  };

  const clientLicenses = (clientId) => licenses.filter(l => l.client_id === clientId);

  const filteredClients = clients.filter(c => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (c.name || "").toLowerCase().includes(term) || (c.company || "").toLowerCase().includes(term) || (c.email || "").toLowerCase().includes(term);
  });

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold" data-testid="clients-title">Gestion des Clients</h1>
        <button onClick={() => { resetForm(); setShowForm(!showForm); }} className="flex items-center gap-2 px-4 py-2 btn-primary rounded-lg" data-testid="add-client-btn">
          <Plus className="w-5 h-5" /> Nouveau client
        </button>
      </div>

      {/* Search */}
      <div className="mb-6 relative">
        <Search className="w-4 h-4 absolute left-3 top-3.5 text-slate-400" />
        <input type="text" placeholder="Rechercher par nom, entreprise ou email..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-3 rounded-lg bg-[#131B2F] border border-white/10 outline-none text-sm" data-testid="client-search" />
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <div className="p-4 rounded-xl bg-[#131B2F] border border-white/10 text-center">
          <div className="text-2xl font-bold text-[#2395E3]">{clients.length}</div>
          <div className="text-xs text-slate-400">Clients</div>
        </div>
        <div className="p-4 rounded-xl bg-[#131B2F] border border-white/10 text-center">
          <div className="text-2xl font-bold text-[#00CCC2]">{licenses.length}</div>
          <div className="text-xs text-slate-400">Licences</div>
        </div>
        <div className="p-4 rounded-xl bg-[#131B2F] border border-white/10 text-center">
          <div className="text-2xl font-bold text-green-400">{licenses.filter(l => l.active).length}</div>
          <div className="text-xs text-slate-400">Actives</div>
        </div>
        <div className="p-4 rounded-xl bg-[#131B2F] border border-white/10 text-center">
          <div className="text-2xl font-bold text-red-400">{licenses.filter(l => l.expires_at && new Date(l.expires_at) < new Date()).length}</div>
          <div className="text-xs text-slate-400">Expirees</div>
        </div>
        <div className="p-4 rounded-xl bg-[#131B2F] border border-white/10 text-center">
          <div className="text-2xl font-bold text-white">{clients.reduce((sum, c) => sum + ((c.ca_tracking?.abonnements_mensuels || 0) + (c.ca_tracking?.abonnements_annuels || 0) + (c.ca_tracking?.services || 0) + (c.ca_tracking?.tma || 0)), 0).toLocaleString("fr-FR")} €</div>
          <div className="text-xs text-slate-400">CA Total</div>
        </div>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="mb-8 p-6 rounded-xl bg-[#131B2F] border border-white/10 space-y-4" data-testid="client-form">
          <div className="grid grid-cols-2 gap-4">
            <input type="text" placeholder="Nom du contact *" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="px-4 py-3 rounded-lg bg-[#0B1121] border border-white/10 outline-none" data-testid="client-name" />
            <input type="text" placeholder="Entreprise" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="px-4 py-3 rounded-lg bg-[#0B1121] border border-white/10 outline-none" data-testid="client-company" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="px-4 py-3 rounded-lg bg-[#0B1121] border border-white/10 outline-none" data-testid="client-email" />
            <input type="text" placeholder="Telephone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="px-4 py-3 rounded-lg bg-[#0B1121] border border-white/10 outline-none" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <input type="text" placeholder="Adresse" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="px-4 py-3 rounded-lg bg-[#0B1121] border border-white/10 outline-none" />
            <input type="text" placeholder="SIRET" value={form.siret} onChange={(e) => setForm({ ...form, siret: e.target.value })} className="px-4 py-3 rounded-lg bg-[#0B1121] border border-white/10 outline-none" />
          </div>
          <textarea placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full px-4 py-3 rounded-lg bg-[#0B1121] border border-white/10 outline-none" rows={2} />

          {/* Partner association */}
          {partners.length > 0 && (
            <div>
              <label className="text-sm text-slate-400 flex items-center gap-2 mb-2"><Users className="w-4 h-4" /> Partenaire associé</label>
              <select value={form.partner_id || ""} onChange={(e) => setForm({ ...form, partner_id: e.target.value })}
                className="w-full px-4 py-3 rounded-lg bg-[#0B1121] border border-white/10 outline-none" data-testid="client-partner-select">
                <option value="">Aucun partenaire (admin uniquement)</option>
                {partners.map(p => (
                  <option key={p.user_id} value={p.user_id}>{p.name || p.email} ({p.email})</option>
                ))}
              </select>
              <p className="text-xs text-slate-500 mt-1">Le partenaire associé ne verra que ce client dans son espace.</p>
            </div>
          )}

          {/* CA Tracking */}
          <div className="border-t border-white/10 pt-4">
            <label className="text-sm text-slate-400 flex items-center gap-2 mb-3"><span className="w-4 h-4 font-bold text-center leading-4">€</span> Suivi Chiffre d'Affaires (€/mois)</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="text-xs text-slate-500 block mb-1">Abo. Mensuels</label>
                <input type="number" step="0.01" min="0" value={form.ca_tracking?.abonnements_mensuels || 0} onChange={(e) => setForm({ ...form, ca_tracking: { ...form.ca_tracking, abonnements_mensuels: parseFloat(e.target.value) || 0 } })}
                  className="w-full px-3 py-2 rounded-lg bg-[#0B1121] border border-white/10 outline-none text-sm" data-testid="ca-mensuel" />
              </div>
              <div>
                <label className="text-xs text-slate-500 block mb-1">Abo. Annuels</label>
                <input type="number" step="0.01" min="0" value={form.ca_tracking?.abonnements_annuels || 0} onChange={(e) => setForm({ ...form, ca_tracking: { ...form.ca_tracking, abonnements_annuels: parseFloat(e.target.value) || 0 } })}
                  className="w-full px-3 py-2 rounded-lg bg-[#0B1121] border border-white/10 outline-none text-sm" data-testid="ca-annuel" />
              </div>
              <div>
                <label className="text-xs text-slate-500 block mb-1">Services</label>
                <input type="number" step="0.01" min="0" value={form.ca_tracking?.services || 0} onChange={(e) => setForm({ ...form, ca_tracking: { ...form.ca_tracking, services: parseFloat(e.target.value) || 0 } })}
                  className="w-full px-3 py-2 rounded-lg bg-[#0B1121] border border-white/10 outline-none text-sm" data-testid="ca-services" />
              </div>
              <div>
                <label className="text-xs text-slate-500 block mb-1">TMA</label>
                <input type="number" step="0.01" min="0" value={form.ca_tracking?.tma || 0} onChange={(e) => setForm({ ...form, ca_tracking: { ...form.ca_tracking, tma: parseFloat(e.target.value) || 0 } })}
                  className="w-full px-3 py-2 rounded-lg bg-[#0B1121] border border-white/10 outline-none text-sm" data-testid="ca-tma" />
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 pt-4">
            <div className="flex justify-between items-center mb-3">
              <label className="text-sm text-slate-400">Sites d'installation</label>
              <button type="button" onClick={addSite} className="text-sm text-[#00CCC2] hover:underline">+ Ajouter un site</button>
            </div>
            {form.sites.map((site, i) => (
              <div key={i} className="p-3 rounded-lg bg-[#0B1121] mb-3 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-400">Site {i + 1}</span>
                  <button type="button" onClick={() => removeSite(i)} className="text-red-400 text-xs">Supprimer</button>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <input type="text" placeholder="URL du site" value={site.url} onChange={(e) => updateSite(i, "url", e.target.value)} className="px-3 py-2 rounded-lg bg-[#131B2F] border border-white/10 outline-none text-sm" />
                  <input type="text" placeholder="Logiciel" value={site.software_name} onChange={(e) => updateSite(i, "software_name", e.target.value)} className="px-3 py-2 rounded-lg bg-[#131B2F] border border-white/10 outline-none text-sm" />
                  <select value={site.deployment_type} onChange={(e) => updateSite(i, "deployment_type", e.target.value)} className="px-3 py-2 rounded-lg bg-[#131B2F] border border-white/10 outline-none text-sm">
                    <option value="onpremise">On-Premise</option>
                    <option value="saas">SaaS</option>
                  </select>
                </div>
                <input type="text" placeholder="Notes" value={site.notes || ""} onChange={(e) => updateSite(i, "notes", e.target.value)} className="w-full px-3 py-2 rounded-lg bg-[#131B2F] border border-white/10 outline-none text-sm" />
              </div>
            ))}
          </div>

          <div className="flex gap-4">
            <button type="submit" className="px-6 py-2 btn-primary rounded-lg" data-testid="client-submit">{editingId ? "Modifier" : "Enregistrer"}</button>
            <button type="button" onClick={resetForm} className="px-6 py-2 rounded-lg border border-white/20">Annuler</button>
          </div>
        </form>
      )}

      <div className="space-y-4">
        {filteredClients.map((client) => {
          const cLicenses = clientLicenses(client.id);
          const isExpanded = expandedClient === client.id;
          return (
            <div key={client.id} className="rounded-xl bg-[#131B2F] border border-white/10 overflow-hidden">
              <div className="flex items-center gap-4 p-4 cursor-pointer" onClick={() => setExpandedClient(isExpanded ? null : client.id)}>
                <div className="p-3 rounded-xl bg-[#0B1121]">
                  <Building className="w-5 h-5 text-[#2395E3]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <h3 className="font-semibold truncate">{client.name}</h3>
                    {client.company && <span className="text-sm text-[#00CCC2]">{client.company}</span>}
                  </div>
                  <div className="flex gap-4 text-xs text-slate-400 mt-1">
                    {client.email && <span>{client.email}</span>}
                    {client.phone && <span>{client.phone}</span>}
                    {client.siret && <span>SIRET: {client.siret}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="px-3 py-1 text-xs rounded-full bg-[#2395E3]/20 text-[#2395E3]">
                    <Key className="w-3 h-3 inline mr-1" />{cLicenses.length}
                  </span>
                  <span className="px-3 py-1 text-xs rounded-full bg-[#00CCC2]/20 text-[#00CCC2]">
                    <Globe className="w-3 h-3 inline mr-1" />{(client.sites || []).length}
                  </span>
                  {client.ca_tracking && ((client.ca_tracking.abonnements_mensuels || 0) + (client.ca_tracking.abonnements_annuels || 0) + (client.ca_tracking.services || 0) + (client.ca_tracking.tma || 0)) > 0 && (
                    <span className="px-3 py-1 text-xs rounded-full bg-green-500/20 text-green-300">
                      <span className="text-xs inline mr-1">€</span>{((client.ca_tracking.abonnements_mensuels || 0) + (client.ca_tracking.abonnements_annuels || 0) + (client.ca_tracking.services || 0) + (client.ca_tracking.tma || 0)).toLocaleString("fr-FR")} €
                    </span>
                  )}
                </div>
                <button onClick={(e) => { e.stopPropagation(); handleEdit(client); }} className="p-2 text-[#00CCC2] hover:bg-[#00CCC2]/20 rounded-lg"><Edit2 className="w-4 h-4" /></button>
                <button onClick={(e) => { e.stopPropagation(); deleteClient(client.id); }} className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                {isExpanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
              </div>

              {isExpanded && (
                <div className="border-t border-white/10 p-4 space-y-4">
                  {/* Client details */}
                  {(client.address || client.siret) && (
                    <div className="flex gap-4 text-sm text-slate-400">
                      {client.address && <span>Adresse: {client.address}</span>}
                      {client.siret && <span>SIRET: {client.siret}</span>}
                    </div>
                  )}

                  {/* CA Tracking */}
                  {client.ca_tracking && (
                    <div>
                      <h4 className="text-sm font-semibold text-slate-300 mb-2 flex items-center gap-2"><span className="w-4 h-4 font-bold text-[#00CCC2] text-center leading-4">€</span> Chiffre d'Affaires</h4>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                        <div className="p-2 rounded-lg bg-[#0B1121] text-center">
                          <div className="text-sm font-bold text-[#2395E3]">{(client.ca_tracking.abonnements_mensuels || 0).toLocaleString("fr-FR")} €</div>
                          <div className="text-xs text-slate-500">Abo. Mensuels</div>
                        </div>
                        <div className="p-2 rounded-lg bg-[#0B1121] text-center">
                          <div className="text-sm font-bold text-[#2395E3]">{(client.ca_tracking.abonnements_annuels || 0).toLocaleString("fr-FR")} €</div>
                          <div className="text-xs text-slate-500">Abo. Annuels</div>
                        </div>
                        <div className="p-2 rounded-lg bg-[#0B1121] text-center">
                          <div className="text-sm font-bold text-[#00CCC2]">{(client.ca_tracking.services || 0).toLocaleString("fr-FR")} €</div>
                          <div className="text-xs text-slate-500">Services</div>
                        </div>
                        <div className="p-2 rounded-lg bg-[#0B1121] text-center">
                          <div className="text-sm font-bold text-[#00CCC2]">{(client.ca_tracking.tma || 0).toLocaleString("fr-FR")} €</div>
                          <div className="text-xs text-slate-500">TMA</div>
                        </div>
                        <div className="p-2 rounded-lg bg-[#0B1121] text-center border border-white/10">
                          <div className="text-sm font-bold text-white">{((client.ca_tracking.abonnements_mensuels || 0) + (client.ca_tracking.abonnements_annuels || 0) + (client.ca_tracking.services || 0) + (client.ca_tracking.tma || 0)).toLocaleString("fr-FR")} €</div>
                          <div className="text-xs text-slate-500">Total</div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Sites */}
                  {client.sites && client.sites.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-slate-300 mb-2">Sites d'installation</h4>
                      <div className="space-y-2">
                        {client.sites.map((site, i) => (
                          <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-[#0B1121] text-sm">
                            {site.deployment_type === "saas" ? <Cloud className="w-4 h-4 text-[#2395E3]" /> : <Server className="w-4 h-4 text-[#00CCC2]" />}
                            <span className="text-white font-medium">{site.software_name || "—"}</span>
                            <span className="text-slate-400">{site.url || "—"}</span>
                            <span className={`ml-auto px-2 py-0.5 text-xs rounded-full ${site.deployment_type === "saas" ? "bg-[#2395E3]/20 text-[#2395E3]" : "bg-[#00CCC2]/20 text-[#00CCC2]"}`}>
                              {site.deployment_type === "saas" ? "SaaS" : "On-Premise"}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Licenses */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="text-sm font-semibold text-slate-300">Licences</h4>
                      <button onClick={() => { setLicForm({ ...licForm, client_id: client.id, generator_id: generators[0]?.id || "" }); setShowLicenseForm(client.id); }} className="text-sm text-[#00CCC2] hover:underline" data-testid="generate-license-btn">
                        + Generer une licence
                      </button>
                    </div>

                    {showLicenseForm === client.id && (
                      <form onSubmit={handleLicenseSubmit} className="p-4 rounded-lg bg-[#0B1121] mb-3 space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs text-slate-400 block mb-1">Generateur / Logiciel</label>
                            <select value={licForm.generator_id} onChange={(e) => setLicForm({ ...licForm, generator_id: e.target.value, license_type: "" })} className="w-full px-3 py-2 rounded-lg bg-[#131B2F] border border-white/10 outline-none text-sm" required data-testid="license-generator-select">
                              <option value="">Choisir...</option>
                              {generators.map(g => <option key={g.id} value={g.id}>{g.name} ({g.prefix}) - {ALGO_LABELS[g.algorithm] || g.algorithm}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="text-xs text-slate-400 block mb-1">URL du site</label>
                            <input type="text" value={licForm.site_url} onChange={(e) => setLicForm({ ...licForm, site_url: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-[#131B2F] border border-white/10 outline-none text-sm" placeholder="https://..." />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div>
                            <label className="text-xs text-slate-400 block mb-1">Deploiement</label>
                            <select value={licForm.deployment_type} onChange={(e) => setLicForm({ ...licForm, deployment_type: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-[#131B2F] border border-white/10 outline-none text-sm">
                              <option value="onpremise">On-Premise</option>
                              <option value="saas">SaaS</option>
                            </select>
                          </div>
                          {selectedGenerator?.has_license_types && (
                            <div>
                              <label className="text-xs text-slate-400 block mb-1">Type de licence</label>
                              <select value={licForm.license_type} onChange={(e) => setLicForm({ ...licForm, license_type: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-[#131B2F] border border-white/10 outline-none text-sm" required>
                                {LICENSE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                              </select>
                            </div>
                          )}
                          <div>
                            <label className="text-xs text-slate-400 block mb-1">Utilisateurs max</label>
                            <input type="number" min="1" value={licForm.max_users} onChange={(e) => setLicForm({ ...licForm, max_users: parseInt(e.target.value) || 1 })} className="w-full px-3 py-2 rounded-lg bg-[#131B2F] border border-white/10 outline-none text-sm" required />
                          </div>
                          {selectedGenerator?.algorithm === "simple-hash" ? (
                            <div>
                              <label className="text-xs text-slate-400 block mb-1">Duree (jours)</label>
                              <input type="number" min="1" value={licForm.duration_days || 365} onChange={(e) => setLicForm({ ...licForm, duration_days: parseInt(e.target.value) || 365 })} className="w-full px-3 py-2 rounded-lg bg-[#131B2F] border border-white/10 outline-none text-sm" required />
                            </div>
                          ) : (
                            <div>
                              <label className="text-xs text-slate-400 block mb-1">Date d'expiration</label>
                              <input type="date" value={licForm.expires_at} onChange={(e) => setLicForm({ ...licForm, expires_at: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-[#131B2F] border border-white/10 outline-none text-sm" required />
                            </div>
                          )}
                        </div>
                        <input type="text" placeholder="Notes (optionnel)" value={licForm.notes} onChange={(e) => setLicForm({ ...licForm, notes: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-[#131B2F] border border-white/10 outline-none text-sm" />
                        <div className="flex gap-3">
                          <button type="submit" className="px-4 py-2 btn-primary rounded-lg text-sm" data-testid="license-submit">Generer la licence</button>
                          <button type="button" onClick={() => setShowLicenseForm(null)} className="px-4 py-2 rounded-lg border border-white/20 text-sm">Annuler</button>
                        </div>
                      </form>
                    )}

                    {cLicenses.length > 0 ? (
                      <div className="space-y-2">
                        {cLicenses.map((lic) => (
                          <div key={lic.id} className={`p-3 rounded-lg ${lic.active ? "bg-[#0B1121]" : "bg-red-500/5 border border-red-500/20"}`}>
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <Key className={`w-4 h-4 shrink-0 ${lic.active ? "text-[#00CCC2]" : "text-red-400"}`} />
                              <span className="text-sm font-medium">{lic.generator_name}</span>
                              {lic.license_type && (
                                <span className="px-2 py-0.5 text-xs rounded-full bg-purple-500/20 text-purple-300 capitalize">{lic.license_type}</span>
                              )}
                              <span className={`px-2 py-0.5 text-xs rounded-full ${lic.deployment_type === "saas" ? "bg-[#2395E3]/20 text-[#2395E3]" : "bg-[#00CCC2]/20 text-[#00CCC2]"}`}>
                                {lic.deployment_type === "saas" ? "SaaS" : "On-Premise"}
                              </span>
                              <span className="text-xs text-slate-400">{lic.max_users} util.</span>
                              {lic.client_code && <span className="text-xs text-slate-500 font-mono">{lic.client_code}</span>}
                              {lic.expires_at && (
                                <span className={`text-xs ${new Date(lic.expires_at) < new Date() ? "text-red-400" : "text-slate-400"}`}>
                                  Exp: {lic.expires_at}
                                </span>
                              )}
                              {lic.site_url && <span className="text-xs text-slate-500 truncate max-w-[200px]">{lic.site_url}</span>}
                              <div className="ml-auto flex gap-1 shrink-0">
                                <button onClick={() => sendLicenseEmail(lic.id)} disabled={sendingEmail === lic.id} className="p-1.5 hover:bg-[#2395E3]/20 rounded-lg" title="Envoyer par email" data-testid={`license-email-${lic.id}`}>
                                  {sendingEmail === lic.id ? <span className="w-4 h-4 block border-2 border-[#2395E3] border-t-transparent rounded-full animate-spin" /> : <Send className="w-4 h-4 text-[#2395E3]" />}
                                </button>
                                <button onClick={() => copyKey(lic.license_key)} className="p-1.5 hover:bg-white/10 rounded-lg" title="Copier">
                                  {copied === lic.license_key ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4 text-slate-400" />}
                                </button>
                                <button onClick={() => toggleLicense(lic.id)} className="p-1.5 hover:bg-white/10 rounded-lg" title={lic.active ? "Desactiver" : "Activer"}>
                                  {lic.active ? <ToggleRight className="w-4 h-4 text-green-400" /> : <ToggleLeft className="w-4 h-4 text-red-400" />}
                                </button>
                                <button onClick={() => deleteLicense(lic.id)} className="p-1.5 hover:bg-red-500/20 rounded-lg"><Trash2 className="w-4 h-4 text-red-400" /></button>
                              </div>
                            </div>
                            <div className="font-mono text-xs text-slate-300 bg-[#131B2F] px-3 py-2 rounded-lg break-all select-all">
                              {lic.license_key}
                            </div>
                            {lic.notes && <p className="text-xs text-slate-500 mt-1">{lic.notes}</p>}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500">Aucune licence pour ce client</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {filteredClients.length === 0 && <p className="text-slate-500 text-center py-8">{searchTerm ? "Aucun client trouve" : "Aucun client enregistre"}</p>}
      </div>
    </div>
  );
};

// ==================== GENERATOR MANAGER ====================
export const GeneratorManager = ({ headers }) => {
  const [generators, setGenerators] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: "", prefix: "CF", secret_key: "", algorithm: "hmac-sha256", has_license_types: false });
  const [showSecret, setShowSecret] = useState({});
  const [showImport, setShowImport] = useState(null);
  const [importJson, setImportJson] = useState("");

  const fetchGenerators = async () => {
    try {
      const res = await axios.get(`${API}/admin/generators`, { headers });
      setGenerators(res.data);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchGenerators(); }, []);

  const resetForm = () => { setForm({ name: "", prefix: "CF", secret_key: "", algorithm: "hmac-sha256", has_license_types: false }); setEditingId(null); setShowForm(false); };

  const handleEdit = (gen) => { setForm({ name: gen.name, prefix: gen.prefix, secret_key: gen.secret_key, algorithm: gen.algorithm, has_license_types: gen.has_license_types || false }); setEditingId(gen.id); setShowForm(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/admin/generators/${editingId}`, form, { headers });
      } else {
        await axios.post(`${API}/admin/generators`, form, { headers });
      }
      resetForm();
      fetchGenerators();
    } catch (err) { console.error(err); }
  };

  const deleteGen = async (id) => {
    if (!window.confirm("Supprimer ce generateur ?")) return;
    await axios.delete(`${API}/admin/generators/${id}`, { headers });
    fetchGenerators();
  };

  const handleImport = async (genId) => {
    try {
      const items = JSON.parse(importJson);
      const arr = Array.isArray(items) ? items : [items];
      const res = await axios.post(`${API}/admin/licenses/import`, { generator_id: genId, items: arr }, { headers });
      alert(`Import reussi: ${res.data.count} licence(s) importee(s)`);
      setShowImport(null);
      setImportJson("");
    } catch (err) {
      alert("Erreur d'import: " + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold" data-testid="generators-title">Generateurs de Licences</h1>
        <button onClick={() => { resetForm(); setShowForm(!showForm); }} className="flex items-center gap-2 px-4 py-2 btn-primary rounded-lg" data-testid="add-generator-btn">
          <Plus className="w-5 h-5" /> Nouveau generateur
        </button>
      </div>

      {/* Info box */}
      <div className="mb-6 p-4 rounded-xl bg-[#131B2F] border border-[#2395E3]/30 text-sm text-slate-300">
        <p className="font-semibold text-[#2395E3] mb-2">3 types de generateurs disponibles :</p>
        <ul className="space-y-1 text-slate-400">
          <li><span className="text-[#00CCC2] font-mono">hmac-sha256</span> — ClaimFlow : cle signee HMAC-SHA256, verifiable cote serveur</li>
          <li><span className="text-[#00CCC2] font-mono">simple-hash</span> — ProjetHub : hash simple + types Standard/Premium/Entreprise + duree en jours</li>
          <li><span className="text-[#00CCC2] font-mono">generic-hmac</span> — Generique : meme algo ClaimFlow avec un prefixe personnalise pour vos futures applis</li>
        </ul>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="mb-8 p-6 rounded-xl bg-[#131B2F] border border-white/10 space-y-4" data-testid="generator-form">
          <div className="grid grid-cols-2 gap-4">
            <input type="text" placeholder="Nom du logiciel (ex: ClaimFlow)" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="px-4 py-3 rounded-lg bg-[#0B1121] border border-white/10 outline-none" data-testid="generator-name" />
            <input type="text" placeholder="Prefixe (ex: CF, PH, MA)" required value={form.prefix} onChange={(e) => setForm({ ...form, prefix: e.target.value.toUpperCase() })} className="px-4 py-3 rounded-lg bg-[#0B1121] border border-white/10 outline-none" data-testid="generator-prefix" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-400 block mb-1">Algorithme</label>
              <select value={form.algorithm} onChange={(e) => setForm({ ...form, algorithm: e.target.value, has_license_types: e.target.value === "simple-hash" })} className="w-full px-4 py-3 rounded-lg bg-[#0B1121] border border-white/10 outline-none" data-testid="generator-algo">
                <option value="hmac-sha256">ClaimFlow (HMAC-SHA256)</option>
                <option value="simple-hash">ProjetHub (Hash simple)</option>
                <option value="generic-hmac">Generique (HMAC personnalise)</option>
              </select>
            </div>
            {form.algorithm !== "simple-hash" && (
              <div>
                <label className="text-xs text-slate-400 block mb-1">Cle secrete HMAC</label>
                <input type="text" placeholder="Cle secrete..." required value={form.secret_key} onChange={(e) => setForm({ ...form, secret_key: e.target.value })} className="w-full px-4 py-3 rounded-lg bg-[#0B1121] border border-white/10 outline-none font-mono" data-testid="generator-secret" />
              </div>
            )}
          </div>
          {form.algorithm === "simple-hash" && (
            <label className="flex items-center gap-2 text-sm text-slate-400">
              <input type="checkbox" checked={form.has_license_types} onChange={(e) => setForm({ ...form, has_license_types: e.target.checked })} className="rounded" />
              Types de licence (Standard / Premium / Entreprise)
            </label>
          )}
          <div className="flex gap-4">
            <button type="submit" className="px-6 py-2 btn-primary rounded-lg" data-testid="generator-submit">{editingId ? "Modifier" : "Enregistrer"}</button>
            <button type="button" onClick={resetForm} className="px-6 py-2 rounded-lg border border-white/20">Annuler</button>
          </div>
        </form>
      )}

      <div className="space-y-4">
        {generators.map((gen) => (
          <div key={gen.id} className="p-4 rounded-xl bg-[#131B2F] border border-white/10">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-[#0B1121]">
                <Key className="w-5 h-5 text-[#00CCC2]" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold">{gen.name}</h3>
                <div className="flex gap-3 text-sm text-slate-400 mt-1">
                  <span>Prefixe: <span className="text-[#2395E3] font-mono">{gen.prefix}</span></span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-white/5">{ALGO_LABELS[gen.algorithm] || gen.algorithm}</span>
                  {gen.has_license_types && <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300">Types S/P/E</span>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {gen.secret_key && (
                  <button onClick={() => setShowSecret({ ...showSecret, [gen.id]: !showSecret[gen.id] })} className="px-3 py-1 text-xs rounded-lg bg-[#0B1121] text-slate-400 hover:text-white">
                    {showSecret[gen.id] ? "Masquer" : "Secret"}
                  </button>
                )}
                <button onClick={() => setShowImport(showImport === gen.id ? null : gen.id)} className="px-3 py-1 text-xs rounded-lg bg-[#0B1121] text-[#00CCC2] hover:text-white flex items-center gap-1">
                  <Upload className="w-3 h-3" /> Import
                </button>
                <button onClick={() => handleEdit(gen)} className="p-2 text-[#00CCC2] hover:bg-[#00CCC2]/20 rounded-lg"><Edit2 className="w-4 h-4" /></button>
                <button onClick={() => deleteGen(gen.id)} className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            {showSecret[gen.id] && gen.secret_key && (
              <div className="mt-3 px-3 py-2 rounded-lg bg-[#0B1121] font-mono text-xs text-slate-300 break-all">
                {gen.secret_key}
              </div>
            )}
            {showImport === gen.id && (
              <div className="mt-3 p-4 rounded-lg bg-[#0B1121] space-y-3">
                <p className="text-xs text-slate-400">
                  Collez ici le JSON de vos licences (depuis localStorage). Format attendu: tableau d'objets avec les champs company, contact, licenseKey, clientCode, maxUsers, expiry, etc.
                </p>
                <textarea value={importJson} onChange={(e) => setImportJson(e.target.value)} className="w-full px-3 py-2 rounded-lg bg-[#131B2F] border border-white/10 outline-none text-xs font-mono" rows={6} placeholder='[{"company":"...", "licenseKey":"...", "maxUsers":5, ...}]' />
                <div className="flex gap-3">
                  <button onClick={() => handleImport(gen.id)} className="px-4 py-2 btn-primary rounded-lg text-sm">Importer</button>
                  <button onClick={() => { setShowImport(null); setImportJson(""); }} className="px-4 py-2 rounded-lg border border-white/20 text-sm">Annuler</button>
                </div>
              </div>
            )}
          </div>
        ))}
        {generators.length === 0 && <p className="text-slate-500 text-center py-8">Aucun generateur. Creez ClaimFlow, ProjetHub ou un generateur generique.</p>}
      </div>
    </div>
  );
};
